import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { trpc, useTRPC } from "../../../shared/api/trpc";

export type DashboardWidget = Awaited<ReturnType<typeof trpc.dashboards.listWidgets.query>>[number];
export type DashboardWidgetConfig = DashboardWidget["config"];
export type DashboardWidgetData = Awaited<ReturnType<typeof trpc.dashboards.widgetData.query>>[number];

// Widgets of a dashboard view plus their computed values.
export function useDashboardWidgets(boardViewId: string | null) {
  const trpcClient = useTRPC();
  const queryClient = useQueryClient();
  const widgetsQuery = useQuery(trpcClient.dashboards.listWidgets.queryOptions(boardViewId ? { boardViewId } : skipToken));
  const dataQuery = useQuery(
    trpcClient.dashboards.widgetData.queryOptions(boardViewId ? { boardViewId } : skipToken, {
      placeholderData: (previous) => previous,
    }),
  );
  const addMutation = useMutation(trpcClient.dashboards.addWidget.mutationOptions());
  const updateMutation = useMutation(trpcClient.dashboards.updateWidget.mutationOptions());
  const removeMutation = useMutation(trpcClient.dashboards.removeWidget.mutationOptions());

  async function reload() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: trpcClient.dashboards.listWidgets.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpcClient.dashboards.widgetData.queryKey() }),
    ]);
  }

  async function addWidget(input: { config: DashboardWidgetConfig; kind: DashboardWidget["kind"]; title: string }) {
    if (!boardViewId) throw new Error("Dashboard view is not selected");
    await addMutation.mutateAsync({ ...input, boardViewId });
    await reload();
  }

  async function updateWidget(input: { config: DashboardWidgetConfig; title: string; widgetId: string }) {
    await updateMutation.mutateAsync(input);
    await reload();
  }

  async function removeWidget(widgetId: string) {
    await removeMutation.mutateAsync({ widgetId });
    await reload();
  }

  return {
    addWidget,
    data: dataQuery.data ?? [],
    isMutating: addMutation.isPending || removeMutation.isPending || updateMutation.isPending,
    removeWidget,
    updateWidget,
    widgets: widgetsQuery.data ?? [],
  };
}
